import getSymbolFromCurrency from "currency-symbol-map";

import { DEFAULT_MONEY_LOCALE } from "@vega-core/constants/global";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";

import { formatNumber, getRoundingAttributes, Rounding } from "./formatNumber";
import { isTruthy } from "./isTruthy";

export function formatMoney(
  money: CoreMoney | null | undefined,
  rounding: Rounding = Rounding.Max2Fractions,
) {
  return money
    ? new Intl.NumberFormat(DEFAULT_MONEY_LOCALE, {
        currency: money.currency,
        currencyDisplay: "narrowSymbol",
        currencySign: "accounting",
        ...getRoundingAttributes(rounding),
        style: "currency",
      }).format(money.amount)
    : "-";
}

/**
 * Formats a money range from a given minimum and maximum money.
 * If only one value is provided, formats it as a single money.
 * If no values are provided, returns an empty string.
 * @param minMoney the minimum money, optional
 * @param maxMoney the maximum money, optional
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X - Y" where X and Y are the formatted money, or just "X" if only one value is provided
 */
export function formatMoneyRange(
  minMoney?: { amount?: number | null; currency?: CoreCurrency | null } | null,
  maxMoney?: { amount?: number | null; currency?: CoreCurrency | null } | null,
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  const hasMinMoney =
    isTruthy(minMoney) &&
    isTruthy(minMoney.amount) &&
    isTruthy(minMoney.currency);
  const hasMaxMoney =
    isTruthy(maxMoney) &&
    isTruthy(maxMoney.amount) &&
    isTruthy(maxMoney.currency);

  if (hasMinMoney && hasMaxMoney) {
    const formattedMin = formatMoney(minMoney as CoreMoney, rounding);
    const formattedMax = formatMoney(maxMoney as CoreMoney, rounding);

    return `${formattedMin} - ${formattedMax}`;
  } else if (hasMinMoney) {
    return formatMoney(minMoney as CoreMoney, rounding);
  } else if (hasMaxMoney) {
    return formatMoney(maxMoney as CoreMoney, rounding);
  } else {
    return "-";
  }
}
/**
 * Type guard for a money range
 * @param value the money range to check
 * @returns true if the money range is valid, false otherwise
 */
export function isMoneyValid(
  value:
    | { amount?: number | null; currency?: CoreCurrency | null }
    | null
    | undefined,
): value is { amount: number; currency: CoreCurrency } {
  return isTruthy(value) && isTruthy(value.amount) && isTruthy(value.currency);
}

/**
 * Formats a money range from a given minimum and maximum money in short format.
 * If only one value is provided, formats it as a single money.
 * If no values are provided, returns an empty string.
 * @param minMoney the minimum money, optional
 * @param maxMoney the maximum money, optional
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X - Y" where X and Y are the formatted money, or just "X" if only one value is provided
 */
export function formatMoneyRangeShort(
  minMoney?: { amount?: number | null; currency?: CoreCurrency | null } | null,
  maxMoney?: { amount?: number | null; currency?: CoreCurrency | null } | null,
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  if (isMoneyValid(minMoney) && isMoneyValid(maxMoney)) {
    const formattedMin = formatMoneyShort(minMoney, rounding);
    const formattedMax = formatMoneyShort(maxMoney, rounding);

    return `${formattedMin} - ${formattedMax}`;
  } else if (isMoneyValid(minMoney)) {
    return formatMoneyShort(minMoney, rounding);
  } else if (isMoneyValid(maxMoney)) {
    return formatMoneyShort(maxMoney, rounding);
  } else {
    return "-";
  }
}

/**
 * Formats a monetary value in a compact short notation with the currency symbol.
 * Returns the formatted string in lowercase.
 * If the monetary value is null or undefined, returns an empty value placeholder.
 *
 * @param money - The monetary value to format, can be null or undefined.
 * @param rounding - The rounding strategy to apply, defaults to maximum 2 fractions.
 * @returns A string representing the formatted monetary value in short notation.
 */

export function formatMoneyShort(
  money: CoreMoney | null | undefined,
  rounding: Rounding = Rounding.Max2Fractions,
) {
  return money
    ? Intl.NumberFormat(DEFAULT_MONEY_LOCALE, {
        compactDisplay: "short",
        currency: money.currency,
        currencyDisplay: "narrowSymbol",
        currencySign: "accounting",
        ...getRoundingAttributes(rounding),
        notation: "compact",
        style: "currency",
      })
        .format(money.amount || 0)
        .toLocaleUpperCase()
    : "-";
}

export function formatMoneyShortWithoutCurrency(
  money: CoreMoney | null | undefined,
  rounding: Rounding = Rounding.NoFractions,
) {
  if (money && money.amount >= 1_000_000) {
    rounding = Rounding.Always1Fractions;
  }

  return money
    ? Intl.NumberFormat(DEFAULT_MONEY_LOCALE, {
        compactDisplay: "short",
        currencySign: "accounting",
        ...getRoundingAttributes(rounding),
        notation: "compact",
      })
        .format(money.amount || 0)
        .toLocaleUpperCase()
    : "-";
}

/**
 * Formats a monetary value in a compact short notation with the currency symbol.
 * Returns the formatted string in lowercase.
 * If the monetary value is null or undefined, returns an empty value placeholder.
 *
 * @param money - The monetary value to format, can be null or undefined.
 * @param minThreshold - The minimum threshold for compact formatting, defaults to 1 million.
 * @param rounding - The rounding strategy to apply, defaults to maximum 2 fractions.
 * @returns A string representing the formatted monetary value in short notation.
 */

export function formatMoneyShortCompact(
  money: CoreMoney | null | undefined,
  minThreshold = 1_000_000,
  rounding: Rounding = Rounding.Max2Fractions,
) {
  if (money && money.amount >= minThreshold) {
    return Intl.NumberFormat(DEFAULT_MONEY_LOCALE, {
      compactDisplay: "short",
      currency: money.currency,
      currencyDisplay: "narrowSymbol",
      currencySign: "accounting",
      ...getRoundingAttributes(rounding),
      notation: "compact",
      style: "currency",
    })
      .format(money.amount)
      .toLocaleUpperCase();
  }

  return money ? formatMoney(money, rounding) : "-";
}

/**
 * Formats a money range from an array of money values in short compact format.
 * Uses compact notation for values above the threshold, otherwise uses standard formatting.
 * If only one value is provided, formats it as a single money.
 * If no values are provided, returns a dash.
 * @param moneyArray the array of money values to format
 * @param minThreshold the minimum threshold for compact formatting, defaults to 1 million
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X - Y" where X and Y are the formatted money, or just "X" if only one value is provided
 */
export function formatMoneyShortCompactRange(
  moneyArray: ({ amount: number; currency: CoreCurrency } | null | undefined)[],
  minThreshold = 1_000_000,
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  const validMoney = moneyArray
    .filter(isMoneyValid)
    .sort((a, b) => a.amount - b.amount);

  if (validMoney.length === 0) {
    return "-";
  } else if (validMoney.length === 1) {
    return formatMoneyShortCompact(validMoney[0], minThreshold, rounding);
  } else {
    const formattedMin = formatMoneyShortCompact(
      validMoney[0],
      minThreshold,
      rounding,
    );
    const formattedMax = formatMoneyShortCompact(
      validMoney[validMoney.length - 1],
      minThreshold,
      rounding,
    );

    return `${formattedMin} - ${formattedMax}`;
  }
}

export const formatMoneyAsMillions = (money: CoreMoney | null | undefined) => {
  if (!money) {
    return "-";
  }

  const symbol = getCurrencySymbol(money.currency);
  const formatted = `${formatNumber(
    Math.abs(money.amount) / 1_000_000,
    Rounding.Always1Fractions,
  )}M`;

  return money.amount < 0 ? `(${symbol}${formatted})` : `${symbol}${formatted}`;
};

export const useDefaultCurrency = () => {
  const { global } = usePublicConfig();

  return global.defaultCurrency;
};

export const useDefaultCurrencySign = () => {
  const currency = useDefaultCurrency();

  return getSymbolFromCurrency(currency);
};

export const getCurrencySymbol = (currency: CoreCurrency) =>
  getSymbolFromCurrency(currency);

export const useDefaultZeroMoney = (): CoreMoney => {
  const defaultCurrency = useDefaultCurrency();

  return { amount: 0, currency: defaultCurrency };
};
